import type { IFetchApi, BrowserWindow, IStatus, ILogger } from '@wix/thunderbolt-symbols'
import type {
	MemberDetailsDTO,
	MemberDetails,
	SocialAuthComponentProps,
	IGetSocialAuthComponentProps,
	ISiteMembersSettings,
	ICaptchaSettings,
	AuthorizedPages,
	IAMPlatformLoginResponse,
	Address,
} from './types'
import type { AUTH_METHODS } from './constants'
import {
	LOGIN_ERROR_CODES,
	SIGN_UP_ERROR_CODES,
	AUTH_RESULT_REASON,
	VERIFICATION_CODE_ERROR_CODES,
	INVISIBLE_CAPTCHA_API_KEY,
} from './constants'
import _ from 'lodash'
import type { IBsiManager } from 'feature-business-logger'
import type { ISessionManager } from 'feature-session-manager'
import type { getRuntimeStyleOverridesManager } from '@wix/thunderbolt-commons'
import { loadScriptTag, getOpenCaptcha } from '@wix/thunderbolt-commons'
import type { StateMachineResponse, StatusV2 } from '@wix/ambassador-iam-authentication-v1-authentication-http/types'
import { Reason } from '@wix/ambassador-iam-authentication-v1-authentication-http/types'
import { format } from 'date-fns'

export const memberDetailsFromDTO = (memberDetailsDTO: MemberDetailsDTO): MemberDetails => ({
	id: memberDetailsDTO.id,
	contactId: memberDetailsDTO.contactId,
	emailVerified: memberDetailsDTO.attributes?.emailVerified,
	role: memberDetailsDTO.memberRole,
	owner: memberDetailsDTO.owner,
	loginEmail: memberDetailsDTO.email,
	memberName: memberDetailsDTO.name ?? memberDetailsDTO.attributes?.name ?? '',
	firstName: memberDetailsDTO.attributes?.firstName,
	lastName: memberDetailsDTO.attributes?.lastName,
	imageUrl: memberDetailsDTO.attributes?.imageUrl ?? '',
	nickname: memberDetailsDTO.attributes?.nickname,
	profilePrivacyStatus: memberDetailsDTO.attributes?.privacyStatus,
	slug: memberDetailsDTO.slug,
	status: memberDetailsDTO.status,
	creationDate: memberDetailsDTO.dateCreated,
	lastUpdateDate: memberDetailsDTO.dateUpdated,
	emails: [],
	phones: [],
	addresses: [],
	labels: [],
	groups: [],
	customFields: [],
	revision: '',
})

const statusFromStatusV2 = (status: StatusV2): IStatus => {
	if (status.name === 'ACTIVE') {
		return 'ACTIVE'
	}
	if (status.name === 'PENDING' && status.reasons?.includes(Reason.PENDING_ADMIN_APPROVAL_REQUIRED)) {
		return 'APPLICANT'
	}
	return 'PENDING'
}

export const memberDetailsFromIAMResponse = (response: IAMPlatformLoginResponse): MemberDetails => {
	const { identity, additionalData } = response
	const { identityProfile } = identity ?? {}
	// Null-safe throughout: this mapper is fed responses typed as StateMachineResponse whose identity
	// shape varies by flow (the SSO LoginResponse carries identityProfile; other state-machine responses
	// may not), so hard `!` assertions here would throw at runtime. Missing fields degrade to empty rather
	// than crash the login. (The completeProfile flow uses memberDetailsFromIAMResponseV2, which also reads
	// owner from metadata.tags instead of additionalData.isOwner.)
	return {
		id: identity?.id!,
		contactId: (additionalData?.contactId as { strValue: string })?.strValue,
		emailVerified: !!(additionalData?.emailVerified as { numValue: number })?.numValue,
		role: (additionalData?.role as { strValue: string })?.strValue,
		owner: !!(additionalData?.isOwner as { numValue: number })?.numValue,
		loginEmail: identity?.email?.address ?? '',
		memberName: identityProfile?.nickname ?? '',
		firstName: identityProfile?.firstName ?? '',
		lastName: identityProfile?.lastName ?? '',
		imageUrl: identityProfile?.picture ?? '',
		nickname: identityProfile?.nickname ?? '',
		profilePrivacyStatus: identityProfile?.privacyStatus!,
		slug: (additionalData?.slug as { strValue: string })?.strValue,
		status: statusFromStatusV2(identity?.status!),
		creationDate: identity?.createdDate?.toString() ?? '',
		lastUpdateDate: identity?.updatedDate?.toString() ?? '',
		emails: [],
		phones: [],
		addresses: [],
		labels: [],
		groups: [],
		customFields: [],
		revision: identity?.revision ?? '',
	}
}

export const hangingPromise = () => new Promise<any>(() => {})

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const getPerformFetch =
	(fetchApi: IFetchApi, requestInit: RequestInit, baseUrl: string) =>
	<T = any>(url: string, options: Partial<RequestInit> = {}): Promise<T> => {
		const headers = {
			...requestInit.headers,
			...(options.body ? { 'Content-Type': 'application/x-www-form-urlencoded' } : {}),
			...options.headers,
			'x-wix-client-artifact-id': 'thunderbolt',
		}
		const optionsWithMergedHeaders = {
			...options,
			...{ headers },
		}

		// TODO: move this transformation into FetchApi
		const absoluteUrl = new URL(url, baseUrl).href

		return fetchApi
			.envFetch(absoluteUrl, { ...requestInit, ...optionsWithMergedHeaders })
			.then(async (response: Response) => {
				const data = await response.json()
				if (!response.ok) {
					// since we can't pass Response object between workers we better transform it now
					throw data
				}

				if (data.errorCode) {
					throw data.errorCode
				}
				return data
			})
	}

export const withFallback = async <T>(request: () => Promise<T>, fallback: () => T) => {
	try {
		return await request()
	} catch (e) {
		return fallback()
	}
}

export const getErrorCode = (error: any) =>
	error?.details?.errorcode ?? error?.details?.errorCode ?? error?.details?.applicationError?.code ?? error

export const isLoginAcceptableError = (error: any) => {
	// In case of approval needed (admin approval / email approval)
	// we cancel the process and this considered an expected error
	if (error === AUTH_RESULT_REASON.CANCELED) {
		return true
	}
	const errorCode = getErrorCode(error)
	return LOGIN_ERROR_CODES.includes(errorCode)
}

export const isSignupAcceptableError = (error: any) => {
	// In case of approval needed (admin approval / email approval)
	// we cancel the process and this considered an expected error
	if (error === AUTH_RESULT_REASON.CANCELED) {
		return true
	}
	const errorCode = getErrorCode(error)
	return SIGN_UP_ERROR_CODES.includes(errorCode)
}

export const isVerificationCodeError = (error: any) => {
	const errorCode = getErrorCode(error)
	return VERIFICATION_CODE_ERROR_CODES.includes(errorCode)
}

const getBsi = (bsiManager?: IBsiManager) => {
	const bsiRaw = bsiManager?.getBsi()
	// The bsi has a weird format: guid|pageNumber, we only need the guid
	return bsiRaw ? bsiRaw.split('|')[0] : '00000000-0000-0000-0000-000000000000'
}

export const getVisitorId = (sessionManager: ISessionManager) =>
	sessionManager.getVisitorId() ?? '00000000-0000-0000-0000-000000000000'
export const _getSocialAuthComponentProps: IGetSocialAuthComponentProps = ({
	config,
	viewerModel,
	sessionManager,
	bsiManager,
	handleSocialLoginResponse,
	isSocialAuthSupported,
	captcha,
	userLanguage,
	reportBi,
	reportSocialAuthStarted,
	useNewSocialFlow,
	translations,
}) => {
	// We don't want the bsi, biVisitorId, and svSession to be cached
	const props: SocialAuthComponentProps = {
		bsi: '00000000-0000-0000-0000-000000000000',
		biVisitorId: '00000000-0000-0000-0000-000000000000',
		svSession: sessionManager.getUserSession()!,
		smCollectionId: config.smcollectionId,
		metaSiteId: viewerModel.site.metaSiteId,
		isSocialAuthSupported,
		// Will be called by the component after mounting the iframe to determine what to send inside via postMessage
		getHostReadyPayload: () => ({
			visitorId: getVisitorId(sessionManager),
			svSession: sessionManager.getUserSession()!,
			bsi: getBsi(bsiManager),
		}),
		openCaptcha: getOpenCaptcha({ captcha, userLanguage }),
		reportBi,
		reportSocialAuthStarted,
		useNewSocialFlow,
		translations,
		onBackendSocialLogin: handleSocialLoginResponse,
	}

	return props
}

const CONTACT_INFO_SYSTEM_FIELDS: Record<string, Object> = {
	id: {},
	firstName: {},
	lastName: {},
	picture: {},
	emails: {},
	addresses: {},
	phones: {},
	labels: {},
	birthdate: {},
	position: {},
	company: {},
	subscription: {},
	vatId: {},
}

const CONTACT_INFO_HIDDEN_SYSTEM_FIELDS: Record<string, Object> = {
	emailVerified: {},
	role: {},
	loginEmail: {},
	nickname: {},
	slug: {},
	language: {},
	status: {},
	creationDate: {},
	lastUpdateDate: {},
	lastLoginDate: {},
	profilePrivacyStatus: {},
}

const customFieldType = (value: any) => {
	if (_.isDate(value)) {
		return 'dateValue'
	} else if (Number.isInteger(value)) {
		return 'numValue'
	} else if (typeof value === 'boolean') {
		return 'boolValue'
	}
	return 'strValue'
}

const serializeCustomValue = (value: any): any => {
	if (Array.isArray(value)) {
		return {
			listValue: {
				value: value.map((item) => serializeCustomValue(item)),
			},
		}
	} else if (value !== null && typeof value === 'object' && !_.isDate(value)) {
		// Object - serialize as mapValue
		return {
			mapValue: {
				value: Object.entries(value).reduce(
					(acc, [key, val]) => {
						acc[key] = serializeCustomValue(val)
						return acc
					},
					{} as Record<string, any>
				),
			},
		}
	} else {
		// Primitive value (string, number, boolean, date)
		return {
			[customFieldType(value)]: value,
		}
	}
}

export const serializeContactInfo = (rawContactInfo: Record<string, any>) =>
	Object.entries(rawContactInfo).reduce(
		(result: Record<string, any>, [key, value]) => {
			const systemField = CONTACT_INFO_SYSTEM_FIELDS[key]
			const hiddenField = CONTACT_INFO_HIDDEN_SYSTEM_FIELDS[key]
			if (systemField) {
				result[key] = value
			} else if (!hiddenField && key) {
				result.customFields.push({
					name: key,
					[customFieldType(value)]: value,
				})
			}
			return result
		},
		{ customFields: [] }
	)

export const convertContactInfoToIdentityProfile = (
	rawContactInfo: Record<string, any>,
	shouldConvertBirthdate: boolean = false
) =>
	Object.entries(rawContactInfo).reduce(
		(result: Record<string, any>, [key, value]) => {
			const systemField = CONTACT_INFO_SYSTEM_FIELDS[key]
			const hiddenField = CONTACT_INFO_HIDDEN_SYSTEM_FIELDS[key]
			if (systemField) {
				if (key === 'birthdate' && shouldConvertBirthdate && (value instanceof Date || isISODate(value))) {
					result[key] = formatDate(value)
				} else if (key === 'phones') {
					result.phonesV2 = (value as Array<string>).map((phone) => ({ phone }))
				} else if (key === 'emails') {
					result.secondaryEmails = (value as Array<string>).map((email) => ({ email }))
				} else if (key === 'addresses') {
					result.addresses = (value as Array<string | Address>).map((address) =>
						typeof address === 'string' ? { address: { addressLine: address } } : { address }
					)
				} else {
					result[key] = value
				}
			} else if (!hiddenField && key) {
				result.customFields.push({
					name: key,
					value: serializeCustomValue(value),
				})
			}
			return result
		},
		{ customFields: [] }
	)

const getGoogleSdkScriptUrl = (language: string, render: string) =>
	`https://www.google.com/recaptcha/enterprise.js?render=${render}&hl=${language}`

export const googleSdkFactory = (
	browserWindow: BrowserWindow,
	runtimeStyleOverridesManager: ReturnType<typeof getRuntimeStyleOverridesManager>
) => {
	const api = {
		async loadScript(language: string, render: string) {
			if (browserWindow?.grecaptcha) {
				return browserWindow.grecaptcha
			}

			return loadScriptTag(getGoogleSdkScriptUrl(language, render))
		},
		showCaptchaBadge() {
			setTimeout(() => {
				runtimeStyleOverridesManager.setItemCssOverrides(
					{ visibility: { value: 'visible' }, 'z-index': { value: 'var(--portals-z-index)' } },
					'.grecaptcha-badge',
					browserWindow as NonNullable<BrowserWindow>
				)
			}, 1000)
		},
		hideCaptchaBadge() {
			runtimeStyleOverridesManager.setItemCssOverrides(
				{ visibility: { value: 'hidden' } },
				'.grecaptcha-badge',
				browserWindow as NonNullable<BrowserWindow>
			)
		},
		setCaptchaBadgeVisibility(shouldShowBadge: boolean) {
			if (shouldShowBadge) {
				return api.showCaptchaBadge()
			}
			api.hideCaptchaBadge()
		},
	}

	return api
}

export const CAPTCHA_SETTINGS_OPTIONS = {
	UNKNOWN: 'UNKNOWN',
	NEVER: 'NEVER',
	SUSPECTED_BOTS_ONLY: 'SUSPECTED_BOTS_ONLY',
	ALWAYS: 'ALWAYS',
}

export const getCaptchaSettings = (smSettings: ISiteMembersSettings): ICaptchaSettings => {
	return {
		invisible: {
			login: smSettings?.loginRecaptchaOption === CAPTCHA_SETTINGS_OPTIONS.SUSPECTED_BOTS_ONLY,
			signup: smSettings?.signupRecaptchaOption === CAPTCHA_SETTINGS_OPTIONS.SUSPECTED_BOTS_ONLY,
		},
		visible: {
			login: smSettings?.loginRecaptchaOption === CAPTCHA_SETTINGS_OPTIONS.ALWAYS,
			signup: smSettings?.signupRecaptchaOption === CAPTCHA_SETTINGS_OPTIONS.ALWAYS,
		},
	}
}

// Max time to wait for grecaptcha.enterprise.ready() before giving up so the auth flow can't hang.
const READY_TIMEOUT_MS = 1000

export const getInvisibleCaptchaTokenFactory =
	(window: BrowserWindow, logger?: ILogger) =>
	async (action: (typeof AUTH_METHODS)[keyof typeof AUTH_METHODS]): Promise<string | undefined> => {
		// Visibility: emit a meter (cheap, aggregatable, + breadcrumb) whenever we fail to produce an
		// invisible token, tagged with the precise cause. This lets us pinpoint WHY the token is missing
		// (script never loaded, enterprise API absent, execute threw, or it resolved empty) rather than
		// only seeing the server-side CAPTCHA_REQUIRED (19971). Only real browsers reach this code path,
		// so it isolates genuine client failures from raw-XHR bots that never run this JS.
		const reportMissing = (reason: string) =>
			logger?.meter(`site-members-invisible-captcha-missing__${action}__${reason}`, undefined, true)
		try {
			const enterprise = window?.grecaptcha?.enterprise
			if (!window?.grecaptcha) {
				reportMissing('no-grecaptcha')
				return undefined
			}
			if (!enterprise) {
				reportMissing('no-enterprise')
				return undefined
			}
			if (!enterprise.execute) {
				reportMissing('no-execute')
				return undefined
			}
			// The reCAPTCHA <script> `onload` does not guarantee the Enterprise API is initialized.
			// Calling `execute` before it is ready resolves to no token, so the auth request is sent
			// with an empty captcha token and the server rejects it with CAPTCHA_REQUIRED (19971).
			// Await `enterprise.ready` first so the invisible token is actually generated — but bound
			// the wait: if `ready` never invokes its callback (partial init / blocked captcha runtime)
			// we must not hang login/register, so time out after 1s and proceed without a token.
			if (typeof enterprise.ready === 'function') {
				const ready = await new Promise<boolean>((resolve) => {
					const timer = setTimeout(() => resolve(false), READY_TIMEOUT_MS)
					enterprise.ready(() => {
						clearTimeout(timer)
						resolve(true)
					})
				})
				if (!ready) {
					reportMissing('ready-timeout')
					return undefined
				}
			}
			const token = await enterprise.execute(INVISIBLE_CAPTCHA_API_KEY, { action })
			if (!token) {
				reportMissing('empty-token')
				return undefined
			}
			return token
		} catch (error) {
			reportMissing('execute-threw')
			logger?.captureError(error as Error, {
				tags: { feature: 'site-members', captcha: 'invisible', action },
				level: 'warning',
			})
			return undefined
		}
	}

export const toAuthorizedPages = (protectedPages: {
	mapValue: {
		value: {
			[pageId: string]: {
				strValue: string
			}
		}
	}
}): AuthorizedPages => _.mapValues(protectedPages?.mapValue?.value ?? [], 'strValue')

export const navigateToAuthentication = (
	browserWindow: BrowserWindow,
	requestUrl: string,
	options: Partial<{ sessionToken: string; error: ISSO_ERRORS }>,
	shouldUseUrlFromBrowserWindowInsteadOfViewerModel: boolean = false
) => {
	try {
		const ourUrl = shouldUseUrlFromBrowserWindowInsteadOfViewerModel ? browserWindow?.location?.href : requestUrl // we take it from the browser, because we don't want the cached url from vieweModel

		if (!ourUrl) {
			return false
		}
		const postLoginRedirect = getAuthorizationUrl(ourUrl, options)
		if (!postLoginRedirect || !postLoginRedirect.href) {
			return false
		}
		browserWindow?.location.replace(postLoginRedirect.href)
		return true
	} catch {
		return false
	}
}

const getAuthorizationUrl = (requestUrl: string, options: Partial<{ sessionToken: string; error: ISSO_ERRORS }>) => {
	try {
		const authorizationUrl = getRawRedirectUrl(requestUrl, REDIRECT_PARAMS.AUTHORIZATION)
		if (options.sessionToken) {
			authorizationUrl.searchParams.set('sessionToken', options.sessionToken)
		}
		if (options.error) {
			authorizationUrl.searchParams.set('error', options.error)
		}
		// To prevent phishing we don't allow a cross origin redirects
		return new URL(authorizationUrl)
	} catch (_error) {
		if (_error?.cause !== REDIRECT_ERRORS.MISSING) {
			console.log('Failed to parse redirect url: ', _error)
		}
		return undefined
	}
}

export const SSO_ERRORS = {
	INVALID_REQUEST: 'invalid_request',
	ACCESS_DENIED: 'access_denied',
	TEMPORARILY_UNAVAILABLE: 'temporarily_unavailable',
} as const

export type ISSO_ERRORS = (typeof SSO_ERRORS)[keyof typeof SSO_ERRORS]

export const REDIRECT_PARAMS = {
	AUTHORIZATION: 'authorization_url',
} as const

export type IREDIRECT_PARAMS = (typeof REDIRECT_PARAMS)[keyof typeof REDIRECT_PARAMS]

const REDIRECT_ERRORS = {
	MISSING: 'missing',
	UNAUTHORIZED: 'unauthorized',
}

export const getRawRedirectUrl = (requestUrl: string, paramName: IREDIRECT_PARAMS) => {
	const url = new URL(requestUrl)
	const postLogin = url.searchParams.get(paramName)
	if (!postLogin) {
		throw new Error('Authorization redirect url missing', { cause: REDIRECT_ERRORS.MISSING })
	}
	const postLoginUrl = new URL(postLogin)
	if (postLoginUrl.hostname !== url.hostname) {
		throw new Error('Authorization redirect url not allowed', { cause: REDIRECT_ERRORS.UNAUTHORIZED })
	}
	return postLoginUrl
}

export const navigateBackToAuthorization = (
	browserWindow: BrowserWindow,
	requestUrl: string,
	shouldUseUrlFromBrowserWindowInsteadOfViewerModel: boolean = false
): void => {
	try {
		const ourUrl = shouldUseUrlFromBrowserWindowInsteadOfViewerModel ? browserWindow?.location?.href : requestUrl // we take it from the browser, because we don't want the cached url from vieweModel
		if (!ourUrl) {
			return
		}
		if (!getRawRedirectUrl(ourUrl, REDIRECT_PARAMS.AUTHORIZATION)) {
			return
		}
		browserWindow?.history.back()
	} catch {
		return
	}
}

export const memberDetailsFromIAMResponseV2 = (resultData?: StateMachineResponse): MemberDetails => {
	const { identity, additionalData } = resultData!
	const { identityProfile, metadata } = identity!
	const isOwner = metadata?.tags?.some((tag) => tag === 'owner')
	return {
		id: identity!.id!,
		contactId: (additionalData?.contactId as { strValue: string })?.strValue,
		emailVerified: identity?.email?.isVerified ?? !!(additionalData?.emailVerified as { numValue: number })?.numValue,
		role: (additionalData?.role as { strValue: string })?.strValue,
		owner: isOwner ?? false,
		loginEmail: identity?.email?.address ?? '',
		memberName: identityProfile?.nickname ?? '',
		firstName: identityProfile?.firstName ?? '',
		lastName: identityProfile?.lastName ?? '',
		imageUrl: identityProfile?.picture ?? '',
		nickname: identityProfile?.nickname ?? '',
		profilePrivacyStatus: identityProfile?.privacyStatus ?? '',
		slug: (additionalData?.slug as { strValue: string })?.strValue,
		status: statusFromStatusV2(identity?.status!),
		creationDate: identity?.createdDate?.toString() ?? '',
		lastUpdateDate: identity?.updatedDate?.toString() ?? '',
		emails: identityProfile?.emails ?? [],
		phones: identityProfile?.phones ?? [],
		addresses: [],
		labels: identityProfile?.labels ?? [],
		groups: [],
		customFields: identityProfile?.customFields ?? [],
		revision: identity?.revision ?? '',
	}
}

export const formatDate = (date: Date) => {
	// birthday date field as a different format then the api expect.
	// in order to pass the valid format we need to convert the date to
	// valid ISO 8601
	// error : is not a valid ISO 8601 local date (YYYY-MM-DD)
	return format(new Date(date), 'yyyy-MM-dd')
}

const isISODate = (str: string) => {
	if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) {
		return false
	}
	const d = new Date(str)
	return d instanceof Date && !isNaN(d.getTime()) && d.toISOString() === str // valid date
}

export const getResetPasswordRedirectUrl = (request: string) => {
	try {
		const url = new URL(request)
		return url.searchParams.get('requestUrl')
	} catch {
		return null
	}
}

/**
 * Validates redirect URLs to prevent XSS attacks via malicious redirect parameters.
 * Allows relative URLs, standard web protocols (http/https), and mobile app schemes.
 *
 * @param redirectUrl - The URL to validate
 * @returns true if the URL is safe to redirect to, false otherwise
 */
export const isValidRedirectUrl = (redirectUrl: string): boolean => {
	if (!redirectUrl || typeof redirectUrl !== 'string') {
		return false
	}

	const trimmedUrl = redirectUrl.trim()
	if (!trimmedUrl) {
		return false
	}

	const lowerCasedUrl = trimmedUrl.toLowerCase()

	// Block dangerous protocols that could lead to XSS
	const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:', 'blob:']
	if (dangerousProtocols.some((protocol) => lowerCasedUrl.startsWith(protocol))) {
		return false
	}

	// Allow relative URLs (same origin)
	if (trimmedUrl.startsWith('/') || trimmedUrl.startsWith('./') || trimmedUrl.startsWith('../')) {
		return true
	}

	// Validate absolute URLs
	try {
		const url = new URL(trimmedUrl)
		const protocol = url.protocol.toLowerCase()

		// Allow standard web protocols
		if (protocol === 'http:' || protocol === 'https:') {
			return true
		}

		// Allow mobile app schemes (RFC 3986 compliant)
		// Mobile schemes typically follow pattern: scheme://host or scheme:path
		if (/^[a-z][a-z0-9+.-]*:$/.test(protocol) && protocol.length >= 3 && protocol.length <= 51) {
			return true
		}

		return false
	} catch {
		// Invalid URL format
		return false
	}
}
